// The SOLUTION GROUPS a picked ManufacturingLineList shows -- what one solution card actually is.
//
// A plan makes the WHOLE section, and it makes each PART independently (a shirt box's Kapak on one line, its
// Kutu on another). So one card is one choice PER PART, and the set of cards is the CARTESIAN PRODUCT across the
// parts -- every complete way to make the section:
//     one part  -> [[s1], [s2], ...]                 (a group of one; every solution is already whole)
//     two parts -> [[kapak1, kutu1], [kapak1, kutu2], ...]
//
// Ranked cheapest COMBINED plan first -- the sum of the parts' scores, which is the same total the group card
// carries as data-score and the same way a plan's best score is the sum of its lines' bests.
//
// THE PER-PART CAP, and when it applies. For a plan of MORE THAN TWO parts the full product explodes as
// value ** parts (three parts of ~30 solutions each is ~27,000 cards), so the product is taken over each part's
// cheapest topNPerPart solutions. One or two parts are NOT capped -- value ** 2 is small enough to list whole (a
// Cover of 35 solutions and a Box of 30 lists all 1,050 pairings). The cap is applied per part rather than to the
// finished list because a group is only as good as its worst half -- slicing the combined list would happily fill
// every slot with one part's cheapest solution paired against the other's, showing no choice at all for that part.
//
// When it applies, the cap is a COST cutoff, and the cost ordering is not neutral about what it hides. A
// die-cutting layout that holds more pieces gangs up less on the press, so it is dearer, so it ranks worse --
// which means a low cap does not sample a part's options, it truncates them at the small end. (The property is
// clearest on the Cover + Box carton of drawing 51517 -- the one-pass die-cut == print options ran from cost-rank
// 2 for a 2-up to rank 35 for a 36-up -- though a two-part plan like that is itself no longer capped.) So the cap
// is deliberately generous, and comes from the environment (ENV FRANK_TOP_N_PER_PART) rather than being compiled
// in: when an arrangement is reported missing from a plan of more than two parts, the fix is to raise the
// variable -- which moves where the truncation lands rather than removing it.

// Every way to pick one item from each array. Copied from the legacy listing's cartesianProduct.
const cartesianProduct = (arrays) => arrays.reduce((accumulator, currentArray) => {
  const result = [];
  accumulator.forEach((existingCombination) => {
    currentArray.forEach((item) => { result.push([...existingCombination, item]); });
  });
  return result;
}, [[]]);

// lower is better: the score is a cost. A missing score sorts last.
const scoreOf = (solution) => (solution.score_data ? solution.score_data.score : Infinity);

// perPart       - one array of solutions per part (the solutions each survive de-duplication and carry their
//                 own score_data.score).
// topNPerPart   - how many of each part's solutions, cheapest first, survive into the product.
//
// Returns the groups, each an array holding one solution per part.
export const combineSolutionGroups = (perPart, topNPerPart) => {
  // One part: each solution is already a whole way to make the section, so there is no product to bound and
  // nothing to cap -- every solution is a card, exactly as a single-part plan has always listed.
  if (perPart.length === 1) { return perPart[0].map((solution) => [solution]); }

  const cheapestFirst = (list) => [...list].sort((a, b) => scoreOf(a) - scoreOf(b));
  // The per-part cap only bounds a plan of MORE THAN TWO parts, where the cartesian product (value ** parts)
  // explodes. One or two parts list their full product -- two parts is value ** 2, small enough to read whole --
  // so every pairing is a card, uncapped.
  const perPartForProduct = perPart.length > 2
    ? perPart.map((list) => cheapestFirst(list).slice(0, topNPerPart))
    : perPart;

  const groupScore = (group) => group.reduce((sum, solution) => sum + scoreOf(solution), 0);

  return cartesianProduct(perPartForProduct).sort((a, b) => groupScore(a) - groupScore(b));
};
